// Fixed-capacity pool of surface-conforming rings and discs (shockwaves, glitch rings, flash cores, damage static,
// ownership sweeps): one instanced draw call. The CPU writes each live ring's shape per frame; free slots have
// alpha 0 and are culled in the vertex shader.

import { BufferAttribute, DynamicDrawUsage, InstancedBufferAttribute, InstancedBufferGeometry, Mesh, type Color, type ShaderMaterial, type Vector3 } from 'three'
import { InvariantViolation } from '@taxwar/net'
import { EMISSIVE_LAYER, assignLayer } from '../render/layers'
import { DirtySpan, additiveMaterial } from './gpu'
import { RING_FRAGMENT, RING_STYLE_CODES, RING_VERTEX, type RingStyle } from './shaders'
import type { VfxUniforms } from './uniforms'

/** Turn resolution: smooth enough for a ring spanning a wide shockwave. */
const RING_SEGMENTS = 64
const VEC3 = 3
const VEC4 = 4

function ringBase(): InstancedBufferGeometry {
  const geometry = new InstancedBufferGeometry()
  const columns = RING_SEGMENTS + 1
  const positions = new Float32Array(columns * 2 * VEC3)
  for (let column = 0; column < columns; column++) {
    const u = column / RING_SEGMENTS
    positions[column * 2 * VEC3] = u
    positions[column * 2 * VEC3 + 1] = 0
    positions[(column * 2 + 1) * VEC3] = u
    positions[(column * 2 + 1) * VEC3 + 1] = 1
  }
  const indices = new Uint16Array(RING_SEGMENTS * 6)
  for (let segment = 0; segment < RING_SEGMENTS; segment++) {
    const inner = segment * 2
    const o = segment * 6
    indices[o] = inner
    indices[o + 1] = inner + 1
    indices[o + 2] = inner + 2
    indices[o + 3] = inner + 1
    indices[o + 4] = inner + 3
    indices[o + 5] = inner + 2
  }
  geometry.setAttribute('position', new BufferAttribute(positions, VEC3))
  geometry.setIndex(new BufferAttribute(indices, 1))
  return geometry
}

function instanced(capacity: number, itemSize: number): InstancedBufferAttribute {
  return new InstancedBufferAttribute(new Float32Array(capacity * itemSize), itemSize).setUsage(DynamicDrawUsage)
}

export class RingPool {
  readonly mesh: Mesh
  private readonly geometry: InstancedBufferGeometry
  private readonly material: ShaderMaterial
  private readonly centers: InstancedBufferAttribute
  private readonly tangents: InstancedBufferAttribute
  private readonly shapes: InstancedBufferAttribute
  private readonly colours: InstancedBufferAttribute
  private readonly styles: InstancedBufferAttribute
  private readonly dirty: DirtySpan

  constructor(
    readonly capacity: number,
    uniforms: VfxUniforms,
  ) {
    this.geometry = ringBase()
    this.centers = instanced(capacity, VEC3)
    this.tangents = instanced(capacity, VEC3)
    this.shapes = instanced(capacity, VEC4)
    this.colours = instanced(capacity, VEC4)
    this.styles = instanced(capacity, VEC4)
    this.geometry.setAttribute('aCenter', this.centers)
    this.geometry.setAttribute('aTangent', this.tangents)
    this.geometry.setAttribute('aShape', this.shapes)
    this.geometry.setAttribute('aColor', this.colours)
    this.geometry.setAttribute('aStyle', this.styles)
    this.geometry.instanceCount = capacity
    this.dirty = new DirtySpan([this.centers, this.tangents, this.shapes, this.colours, this.styles])
    this.material = additiveMaterial(RING_VERTEX, RING_FRAGMENT, {
      uTime: uniforms.uTime,
      uGlitch: uniforms.uGlitch,
      uGlitchSteps: uniforms.uGlitchSteps,
      uMotion: uniforms.uMotion,
      uStaticSteps: uniforms.uStaticSteps,
    })
    this.mesh = new Mesh(this.geometry, this.material)
    // Ring vertices are placed in the shader; a cached bounding sphere would cull them wrongly.
    this.mesh.frustumCulled = false
    assignLayer(this.mesh, EMISSIVE_LAYER)
  }

  /**
   * Fixes a slot's placement and look for its lifetime: unit normal, tangent ⟂ normal, style, dashes, glitch, and
   * tint × intensity (may exceed 1 for hot glow). The slot stays hidden until animate() gives it alpha.
   */
  place(slot: number, center: Vector3, tangent: Vector3, style: RingStyle, dashes: number, glitch: number, seed: number, colour: Color, intensity: number): void {
    this.check(slot)
    this.centers.setXYZ(slot, center.x, center.y, center.z)
    this.tangents.setXYZ(slot, tangent.x, tangent.y, tangent.z)
    this.shapes.setW(slot, dashes)
    this.colours.setXYZW(slot, colour.r * intensity, colour.g * intensity, colour.b * intensity, 0)
    this.styles.setXYZ(slot, RING_STYLE_CODES[style], glitch, seed)
    this.dirty.mark(slot, 1)
  }

  /** Per-frame shape: angular radii (radians), radius of the sphere it lies on, brightness, sweep progress. */
  animate(slot: number, innerAngle: number, outerAngle: number, altitude: number, alpha: number, sweep: number): void {
    this.shapes.setXYZ(slot, innerAngle, outerAngle, altitude)
    this.colours.setW(slot, alpha)
    this.styles.setW(slot, sweep)
    this.dirty.mark(slot, 1)
  }

  hide(slot: number): void {
    this.check(slot)
    this.colours.setW(slot, 0)
    this.dirty.mark(slot, 1)
  }

  upload(): void {
    this.dirty.flush()
  }

  dispose(): void {
    this.mesh.removeFromParent()
    this.geometry.dispose()
    this.material.dispose()
  }

  private check(slot: number): void {
    if (slot < 0 || slot >= this.capacity) throw new InvariantViolation(`ring slot ${slot} outside the pool of ${this.capacity}`)
  }
}
